import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import DropdownSelect from 'components/Dropdown/DropdownSelect';
import FormResult from 'components/Form/FormResult';
import SectionHeader from 'components/SectionHeader';
import TableStatic from 'components/Table/TableStatic';
import useBeforeUnload from 'hooks/useBeforeUnload';
import {useBackend} from 'state/backend';
import {FormFields} from 'types/utils';
import {onApiFormError, onApiKeySuccess} from 'utils/request/result';
import {useChangeCompanyEmail} from '../services';
import {Email, Employee} from '../types';

type NotificationAddressesFormData = FormFields<Record<string, string>>;

export type NotificationAddressesProps = {
    emails: Array<Email>;
    notificationAddresses: Array<Employee>;
};

const NotificationAddresses: FC<NotificationAddressesProps> = ({
    emails,
    notificationAddresses,
}) => {
    const {refreshData} = useBackend();

    const {t} = useTranslation('Profile.EmailSettings');

    const methods = useForm<NotificationAddressesFormData>({
        defaultValues: Object.fromEntries(
            notificationAddresses.map((address) => [
                address.mid,
                address.email,
            ]),
        ),
    });
    const {
        formState: {isDirty},
        getValues,
        reset,
        setValue,
    } = methods;

    useBeforeUnload(isDirty);

    const {buttonProps, mutateAsync} = useChangeCompanyEmail();

    const onSubmit = (entry: Employee) => {
        const targetEmailMID = emails.find(
            (email) => email.email === getValues(entry.mid),
        )?.mid;
        if (!targetEmailMID) return;

        mutateAsync({
            email: targetEmailMID,
            employee: entry.mid,
            requestId: entry.mid,
        })
            .then(onApiKeySuccess(methods))
            .catch(onApiFormError(methods))
            .finally(async () => {
                await refreshData('/profile/email-settings/');
                reset(getValues(), {keepErrors: true});
            });
    };

    const getEmailOptions = () =>
        emails.map(({disabled, email, status}) => ({
            disabled,
            label: email,
            status,
            value: email,
        }));

    const onChangeEmail = (entry: Employee) => (email?: string) => {
        if (email) setValue(entry.mid, email);
    };

    return (
        <FormProvider {...methods}>
            <form>
                <SectionHeader
                    className="mt-6 mb-2"
                    hideHR={true}
                    level={2}
                    subtitle={
                        <Trans
                            components={[
                                <a key={0} href="/notification/">
                                    {' '}
                                </a>,
                            ]}
                            i18nKey="notificationAddresses.description"
                            ns="Profile.EmailSettings"
                            t={t}
                        />
                    }
                    title={t('notificationAddresses.title')}
                />
                <FormResult className="my-2" />
                <TableStatic aria-label={t('notificationAddresses.title')}>
                    <TableStatic.Header>
                        <TableStatic.HeaderRow>
                            <TableStatic.Column>
                                {t('notificationAddresses.company')}
                            </TableStatic.Column>
                            <TableStatic.Column>
                                {t('notificationAddresses.email')}
                            </TableStatic.Column>
                            <TableStatic.Column />
                        </TableStatic.HeaderRow>
                    </TableStatic.Header>
                    <TableStatic.Body>
                        {notificationAddresses.map((entry) => (
                            <TableStatic.Row key={entry.mid}>
                                <TableStatic.Cell>
                                    {entry.company}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <DropdownSelect
                                        aria-label={`${entry.company}-email`}
                                        hasClearSelection={false}
                                        isCompact={true}
                                        isSearchable={true}
                                        name={entry.mid}
                                        onChange={onChangeEmail(entry)}
                                        options={getEmailOptions()}
                                    />
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <div className="flex flex-row-reverse">
                                        <Button
                                            aria-label={`${entry.company} button`}
                                            disabled={
                                                getValues(entry.mid) ===
                                                entry.email
                                            }
                                            kind="secondary"
                                            onClick={() => onSubmit(entry)}
                                            size="sm"
                                            {...buttonProps[entry.mid]}
                                        >
                                            {t('notificationAddresses.save')}
                                        </Button>
                                    </div>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))}
                    </TableStatic.Body>
                </TableStatic>
            </form>
        </FormProvider>
    );
};

export default NotificationAddresses;
